import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { isSuperAdminEmail } from '@/config/platform';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Bell,
  Settings,
  User,
  LogOut,
  ChevronDown,
  Shield,
  Home,
  BarChart3,
  Users,
  Calendar,
  MessageSquare,
  Info,
  Cog,
  Key,
  Search,
  LayoutGrid
} from 'lucide-react';
import PhygitalIcon from '@/components/PhygitalIcon';
import { DefaultAvatar } from '@/components/DefaultAvatars';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UnifiedTopBar - Consistent navigation bar across Admin, Host Dashboard, and /me
 * 
 * Props:
 * - variant: 'admin' | 'host' | 'user' - determines which view/links to show
 * - currentUser: user object with name, email, avatar, role, notifications
 * - eventName: (host variant) name of the event being managed
 * - eventLogo: (host variant) logo/icon for the event
 * - onBack: (host variant) callback for back button
 */
export default function UnifiedTopBar({ 
  variant = 'user', 
  currentUser: propUser,
  eventName,
  eventLogo,
  onBack,
  notificationCount = 0
}) {
  const navigate = useNavigate();
  const { logout, user: authUser } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Always use the authenticated user for identity (avatar, name, email, role checks)
  // propUser may be event host data which doesn't match the logged-in account
  const currentUser = authUser || propUser;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  // Super Admin access: Check is_super_admin field (set by admin), role, OR specific email
  const isSuperAdmin = currentUser?.is_super_admin === true || 
                       currentUser?.role === 'super_admin' || 
                       (currentUser?.email && isSuperAdminEmail(currentUser.email));

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="flex h-14 items-center px-4 gap-4">
        {/* Left: Logo/Back + Context */}
        <div className="flex items-center gap-3">
          {variant === 'host' && onBack ? (
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
              <ChevronDown className="h-4 w-4 rotate-90" />
              Back
            </Button>
          ) : (
            <Link to="/me" className="flex items-center gap-2">
              <PhygitalIcon size={28} />
              <span className="font-semibold text-lg hidden sm:inline">phygital</span>
            </Link>
          )}

          {/* Event context for host variant */}
          {variant === 'host' && eventName && (
            <div className="flex items-center gap-2 ml-2 pl-2 border-l">
              {eventLogo && (
                <img src={eventLogo} alt="" className="h-6 w-6 rounded object-cover" />
              )}
              <span className="font-medium text-sm hidden md:inline">{eventName}</span>
            </div>
          )}
        </div>

        {/* Center: Empty for /me pages (tabs are on the page), Host nav for host variant */}
        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {variant === 'host' && (
            <>
              <Button variant="ghost" size="sm">Overview</Button>
              <Button variant="ghost" size="sm">RSVPs</Button>
              <Button variant="ghost" size="sm">Insights</Button>
              <Button variant="ghost" size="sm">Settings</Button>
            </>
          )}
        </nav>

        {/* Right: Settings + Admin + Notifications + Avatar (Iter 9.47.u - search removed per user feedback) */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Settings gear - goes to /me settings */}
          <Button variant="ghost" size="sm" asChild title="Settings" data-testid="settings-gear-btn">
            <Link to="/me/settings">
              <Settings className="h-5 w-5 text-gray-500" />
            </Link>
          </Button>

          {/* Notifications */}
          <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative" data-testid="notifications-btn">
                <Bell className="h-5 w-5" />
                {notificationCount > 0 && (
                  <Badge 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500"
                  >
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel>Notifications</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-64 overflow-y-auto">
                <div className="p-3 text-sm text-gray-500 text-center">
                  No new notifications
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/me/settings?tab=notifications" className="w-full text-center text-sm">
                  Notification Settings
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Avatar + Menu (per Master Plan: My Profile, Settings, Sign Out) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 px-1" data-testid="topbar-avatar-link">
                {currentUser?.profile_image ? (
                  <img 
                    src={currentUser.profile_image} 
                    alt="" 
                    className="h-8 w-8 rounded-full object-cover ring-2 ring-transparent hover:ring-purple-300 transition-all cursor-pointer"
                  />
                ) : (
                  <DefaultAvatar 
                    firstName={currentUser?.first_name || currentUser?.name?.split(' ')[0]} 
                    lastName={currentUser?.last_name || currentUser?.name?.split(' ')[1]} 
                    size={32}
                    className="ring-2 ring-transparent hover:ring-gray-300 transition-all cursor-pointer"
                  />
                )}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{currentUser?.first_name} {currentUser?.last_name}</span>
                  <span className="text-xs font-normal text-gray-500">{currentUser?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/me" className="cursor-pointer">
                  <User className="h-4 w-4 mr-2" /> My Profile
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer">
                      <Shield className="h-4 w-4 mr-2" /> Admin Panel
                    </Link>
                  </DropdownMenuItem>
                  {isSuperAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/settings" className="cursor-pointer" data-testid="admin-settings-link">
                        <Key className="h-4 w-4 mr-2" /> Admin Settings
                      </Link>
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
